import React, { useState, useRef, useEffect, useCallback, forwardRef } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import { showcaseItemsForLanding } from "./landingContent";
import { LandingAbstractMockup } from "./LandingAbstractMockups";

const SHOWCASE_AUTO_SCROLL_MS = 5500;

const ShowcaseCard = forwardRef(function ShowcaseCard({ item }, ref) {
  const base = String(import.meta.env.BASE_URL || "/").replace(/\/?$/, "/");
  const src = item.useScreenshot && item.imageFile ? `${base}landing/showcase/${item.imageFile}` : null;
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = src && !imgFailed;

  return (
    <article ref={ref} className="hr-landing-showcase-card">
      <div className="hr-landing-showcase-card__frame">
        <div className="hr-landing-browser">
          <div className="hr-landing-browser__chrome" aria-hidden>
            <span className="hr-landing-browser__dot" />
            <span className="hr-landing-browser__dot hr-landing-browser__dot--mid" />
            <span className="hr-landing-browser__dot hr-landing-browser__dot--green" />
          </div>
          <div className="hr-landing-browser__viewport">
            {showImage ? (
              <img
                src={src}
                alt=""
                className="hr-landing-browser__shot"
                loading="lazy"
                onError={() => setImgFailed(true)}
              />
            ) : (
              <div className="hr-landing-browser__mock">
                <LandingAbstractMockup variant={item.mockupVariant} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="hr-landing-showcase-card__meta">
        <span className="hr-landing-showcase-card__badge">{item.badge}</span>
        <h3 className="hr-landing-showcase-card__title">{item.title}</h3>
        <p className="hr-landing-showcase-card__caption">{item.caption}</p>
      </div>
    </article>
  );
});

ShowcaseCard.displayName = "ShowcaseCard";

export default function LandingProductShowcase() {
  const itemRefs = useRef([]);
  const slideIndexRef = useRef(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [pauseAuto, setPauseAuto] = useState(false);
  const showcaseItems = showcaseItemsForLanding;
  const count = showcaseItems.length;

  /** inline: start מיישר לקצה התחלה של מיכל הגלילה — עובד טוב ב־RTL ומחזיר לכרטיס ראשון אחרי האחרון. */
  const scrollToSlideIndex = useCallback((i) => {
    itemRefs.current[i]?.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });
  }, []);

  const goToSlide = useCallback(
    (i) => {
      const next = ((i % count) + count) % count;
      slideIndexRef.current = next;
      setSlideIndex(next);
      requestAnimationFrame(() => scrollToSlideIndex(next));
    },
    [count, scrollToSlideIndex]
  );

  useEffect(() => {
    slideIndexRef.current = slideIndex;
  }, [slideIndex]);

  useEffect(() => {
    if (pauseAuto || count <= 1) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const tick = () => {
      const next = (slideIndexRef.current + 1) % count;
      slideIndexRef.current = next;
      setSlideIndex(next);
      requestAnimationFrame(() => scrollToSlideIndex(next));
    };

    const id = window.setInterval(tick, SHOWCASE_AUTO_SCROLL_MS);
    return () => window.clearInterval(id);
  }, [pauseAuto, count, scrollToSlideIndex]);

  return (
    <section
      className="hr-landing-section hr-landing-section--showcase"
      aria-labelledby="hr-landing-showcase-title"
    >
      <div className="home-page__container">
        <header className="hr-landing-section__head hr-landing-section__head--center">
          <h2 id="hr-landing-showcase-title" className="hr-landing-section__title">
            תראו את המערכת מבפנים
          </h2>
          <p className="hr-landing-section__lead hr-landing-section__lead--narrow">
            מבט על חלק מהמסכים המרכזיים במערכת — חיפוש מלונות, חבילות נופש ודשבורד הסוכן.
          </p>
        </header>
        <div
          className="hr-landing-showcase-carousel"
          role="region"
          aria-roledescription="קרוסלה"
          aria-label="תצוגת מסכי המערכת"
          onMouseEnter={() => setPauseAuto(true)}
          onMouseLeave={() => setPauseAuto(false)}
          onFocusCapture={() => setPauseAuto(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPauseAuto(false);
          }}
        >
          {count > 1 ? (
            <>
              <button
                type="button"
                className="hr-landing-showcase-nav hr-landing-showcase-nav--prev"
                aria-label="שקופית קודמת"
                onClick={() => goToSlide(slideIndex - 1)}
              >
                <HiChevronRight className="hr-landing-showcase-nav__icon" aria-hidden />
              </button>
              <button
                type="button"
                className="hr-landing-showcase-nav hr-landing-showcase-nav--next"
                aria-label="שקופית הבאה"
                onClick={() => goToSlide(slideIndex + 1)}
              >
                <HiChevronLeft className="hr-landing-showcase-nav__icon" aria-hidden />
              </button>
            </>
          ) : null}
          <div className="hr-landing-showcase-scroll">
            <div className="hr-landing-showcase-track">
              {showcaseItems.map((item, i) => (
                <ShowcaseCard
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  item={item}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
